import { DEFAULT_THEME_SETTINGS } from '../theme';
import themeSettingsAPI from './api/themeSettingsAPI';

const THEME_CACHE_KEY = 'latthsaab:theme-settings';

function toRgb(hexColor) {
  const normalized = hexColor.replace('#', '');
  const value = parseInt(normalized, 16);

  return {
    red: (value >> 16) & 255,
    green: (value >> 8) & 255,
    blue: value & 255
  };
}

function toRgba(hexColor, alpha) {
  const { red, green, blue } = toRgb(hexColor);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function getMergedThemeSettings(themeSettings = {}) {
  return Object.keys(DEFAULT_THEME_SETTINGS).reduce((accumulator, key) => {
    accumulator[key] = typeof themeSettings[key] === 'string' && themeSettings[key]
      ? themeSettings[key]
      : DEFAULT_THEME_SETTINGS[key];
    return accumulator;
  }, {});
}

export function buildThemeVariableMap(themeSettings = {}) {
  const theme = getMergedThemeSettings(themeSettings);

  return {
    '--primary': theme.primary,
    '--primary-dark': theme.primaryDark,
    '--primary-light': theme.primaryLight,
    '--secondary': theme.secondary,
    '--secondary-dark': theme.secondaryDark,
    '--secondary-light': theme.secondaryLight,
    '--accent': theme.accent,
    '--accent-light': theme.accentLight,
    '--bg-primary': theme.bgPrimary,
    '--bg-secondary': theme.bgSecondary,
    '--bg-tertiary': theme.bgTertiary,
    '--bg-card': theme.bgPrimary,
    '--text-primary': theme.textPrimary,
    '--text-secondary': theme.textSecondary,
    '--text-tertiary': theme.textTertiary,
    '--black': theme.textPrimary,
    '--white': theme.bgPrimary,
    '--success': theme.success,
    '--warning': theme.warning,
    '--danger': theme.danger,
    '--info': theme.info,
    '--gradient-primary': `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
    '--gradient-dark': `linear-gradient(135deg, ${theme.primaryDark} 0%, ${theme.primary} 100%)`,
    '--gradient-light': `linear-gradient(135deg, ${theme.bgPrimary} 0%, ${theme.bgTertiary} 100%)`,
    '--shadow-hover': `0 18px 38px ${toRgba(theme.primary, 0.16)}`,
    '--shadow-base': `0 10px 28px ${toRgba(theme.textPrimary, 0.08)}`,
    '--shadow-md': `0 16px 36px ${toRgba(theme.textPrimary, 0.1)}`,
    '--shadow-lg': `0 22px 50px ${toRgba(theme.textPrimary, 0.12)}`,
    '--border-color': toRgba(theme.primary, 0.14),
    '--color-primary': theme.primary,
    '--color-secondary': theme.secondary,
    '--color-accent': theme.accent,
    '--text-white': theme.textPrimary,
    '--text-muted': theme.textSecondary
  };
}

export function applyThemeSettings(themeSettings = {}) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const variables = buildThemeVariableMap(themeSettings);

  Object.entries(variables).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

export function cacheThemeSettings(themeSettings = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(getMergedThemeSettings(themeSettings)));
}

export function loadCachedThemeSettings() {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME_SETTINGS;
  }

  try {
    const raw = window.localStorage.getItem(THEME_CACHE_KEY);
    return raw ? getMergedThemeSettings(JSON.parse(raw)) : DEFAULT_THEME_SETTINGS;
  } catch {
    return DEFAULT_THEME_SETTINGS;
  }
}

export async function loadAndApplyThemeSettings() {
  const cached = loadCachedThemeSettings();
  applyThemeSettings(cached);

  try {
    const response = await themeSettingsAPI.getPublicTheme();
    if (response.success && response.data) {
      const merged = getMergedThemeSettings(response.data);
      applyThemeSettings(merged);
      cacheThemeSettings(merged);
      return merged;
    }
  } catch (error) {
    console.error('Failed to load theme settings:', error);
  }

  return cached;
}